import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { notifyUser, showBrowserNotification } from '../utils/notifications';

const WebSocketContext = createContext(null);

// Get WebSocket URL from backend URL
const getWebSocketUrl = (token) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Convert http(s) to ws(s)
  const wsUrl = backendUrl.replace(/^http/, 'ws');
  return `${wsUrl}/api/ws/${token}`;
};

export function WebSocketProvider({ children }) {
  const { user, token } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const messageHandlersRef = useRef(new Map());

  // Register a handler for a specific message type
  const registerHandler = useCallback((messageType, handler) => {
    if (!messageHandlersRef.current.has(messageType)) {
      messageHandlersRef.current.set(messageType, new Set());
    }
    messageHandlersRef.current.get(messageType).add(handler);
    
    // Return unregister function
    return () => {
      const handlers = messageHandlersRef.current.get(messageType);
      if (handlers) {
        handlers.delete(handler);
      }
    };
  }, []);

  // Dispatch message to all registered handlers
  const dispatchMessage = useCallback((message) => {
    const handlers = messageHandlersRef.current.get(message.type);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error('Handler error:', error);
        }
      });
    }
    
    // Also dispatch to 'all' handlers
    const allHandlers = messageHandlersRef.current.get('all');
    if (allHandlers) {
      allHandlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error('Handler error:', error);
        }
      });
    }
  }, []);

  // Send activity update via WebSocket
  const sendActivity = useCallback((currentPage, pageTitle) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'activity',
        current_page: currentPage,
        page_title: pageTitle
      }));
    }
  }, []);

  // Send ping to keep connection alive
  const sendPing = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!token || !user) {
      return;
    }

    // Don't reconnect if already connected or connecting
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    setConnectionStatus('connecting');
    
    try {
      const wsUrl = getWebSocketUrl(token);
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        setConnectionStatus('connected');
        reconnectAttempts.current = 0;
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          setLastMessage(message);
          dispatchMessage(message);

          // Handle specific message types with toasts and notifications
          if (message.type === 'job_offer') {
            // New job for provider - sound + vibration
            notifyUser('newJob');
            showBrowserNotification('New Job Available!', {
              body: `${message.service_name} - $${message.total_pay}`,
              type: 'newJob'
            });
            toast.success(`New Job Available! ${message.service_name} - $${message.total_pay}`, {
              duration: 10000,
              description: message.message
            });
          } else if (message.type === 'job_assigned') {
            // Provider assigned to job
            notifyUser('jobAccepted');
            showBrowserNotification('Job Assigned!', {
              body: message.message,
              type: 'jobAccepted'
            });
            toast.success('Job Assigned!', {
              description: message.message
            });
          } else if (message.type === 'request_status_change') {
            notifyUser('message');
            toast.info(`Status Update: ${message.new_status}`, {
              description: message.message
            });
          } else if (message.type === 'new_notification') {
            notifyUser('message');
            toast(message.title, {
              description: message.body
            });
          } else if (message.type === 'new_request' && (user?.is_admin || user?.is_owner)) {
            // New request for admin/owner
            notifyUser('newJob');
            showBrowserNotification('New Service Request', {
              body: `${message.service_name} - $${message.total_price}`,
              type: 'newJob'
            });
            toast.info(`New Request: ${message.service_name}`, {
              description: `$${message.total_price}`
            });
          } else if (message.type === 'payment_received' || message.type === 'cancellation_fee_received') {
            // Payment notification
            notifyUser('payment');
            showBrowserNotification('Payment Received!', {
              body: message.message,
              type: 'payment'
            });
            toast.success(message.message);
          } else if (message.type === 'provider_cancelled' || message.type === 'job_cancellation_pending') {
            // Cancellation alert
            notifyUser('alert');
            toast.warning(message.message);
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('WebSocket disconnected', event.code, event.reason);
        setIsConnected(false);
        setConnectionStatus('disconnected');
        wsRef.current = null;

        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
          setConnectionStatus('reconnecting');
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
      };

    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      setConnectionStatus('error');
    }
  }, [token, user, dispatchMessage]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    
    setIsConnected(false);
    setConnectionStatus('disconnected');
  }, []);

  // Connect when user logs in
  useEffect(() => {
    if (token && user) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [token, user, connect, disconnect]);

  // Ping interval to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(sendPing, 30000);
    return () => clearInterval(pingInterval);
  }, [isConnected, sendPing]);

  const value = {
    isConnected,
    connectionStatus,
    lastMessage,
    sendActivity,
    sendPing,
    registerHandler,
    connect,
    disconnect
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

export default WebSocketContext;
